import { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from "recharts";

const COLORS = ["#6366f1", "#f59e0b", "#10b981"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#f8fafc", border: "1px solid #e2e8f0",
      borderRadius: 8, padding: "10px 14px", minWidth: 190
    }}>
      <p style={{ color: "#94a3b8", marginBottom: 6, fontSize: 12 }}>Día {label}</p>
      {payload.map((entry, i) => (
        entry.value != null && (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 3 }}>
            <span style={{ color: entry.color, fontSize: 12 }}>{entry.name}</span>
            <span style={{ color: "#1e293b", fontWeight: 600, fontSize: 13 }}>
              ${entry.value.toLocaleString("en-US", { minimumFractionDigits: 0 })}
            </span>
          </div>
        )
      ))}
    </div>
  );
};

export default function DailySalesChart({ token, apiBase = "/api" }) {
  const [data, setData] = useState(null);
  const [selectedStore, setSelectedStore] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = new Date();
  const [refYear, setRefYear] = useState(today.getFullYear());
  const [refMonth, setRefMonth] = useState(today.getMonth() + 1);

  const fetchData = async (storeId = "", year = null, month = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (storeId) params.append("store_id", storeId);
      if (year) params.append("ref_year", year);
      if (month) params.append("ref_month", month);
      const url = `${apiBase}/reports/daily-sales-comparison${params.toString() ? "?" + params.toString() : ""}`;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedStore);
  }, []);

  const monthKeys = data ? Object.keys(data.months) : [];

  const chartData = useMemo(() => {
    if (!data) return [];
    const rows = [];
    for (let day = 1; day <= 31; day++) {
      const row = { day };
      let hasData = false;
      monthKeys.forEach(mk => {
        const val = data.months[mk].days[day]?.total_usd ?? null;
        row[mk] = val;
        if (val !== null) hasData = true;
      });
      if (hasData) rows.push(row);
    }
    return rows;
  }, [data]);

  const currentMonthKey = monthKeys[0];
  const currentMonth = data?.months?.[currentMonthKey];
  const prevMonthKey = monthKeys[1];
  const peakDay = currentMonth?.peak_day;

  const varPct = (currentMonth && prevMonthKey && data.months[prevMonthKey]?.total_month_usd > 0)
    ? Math.round(((currentMonth.total_month_usd - data.months[prevMonthKey].total_month_usd)
        / data.months[prevMonthKey].total_month_usd) * 100)
    : null;

  if (error) return null;

  return (
    <div style={{
      background: "#ffffff", border: "1px solid #e2e8f0",
      borderRadius: 14, padding: "24px 20px", color: "#f1f5f9",
      fontFamily: "inherit"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
            📈 Ventas Diarias — Comparación 3 Meses
          </h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>
            Identifica los días de mayor movimiento por mes
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={refMonth} onChange={e => { const v = Number(e.target.value); setRefMonth(v); fetchData(selectedStore, refYear, v); }}
            style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b", padding: "6px 10px", fontSize: 13, cursor: "pointer", outline: "none" }}>
            {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"].map((m,i) => (
              <option key={i+1} value={i+1}>{m}</option>
            ))}
          </select>
          <select value={refYear} onChange={e => { const v = Number(e.target.value); setRefYear(v); fetchData(selectedStore, v, refMonth); }}
            style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b", padding: "6px 10px", fontSize: 13, cursor: "pointer", outline: "none" }}>
            {[today.getFullYear(), today.getFullYear()-1, today.getFullYear()-2].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {data?.stores?.length > 0 && (
            <select value={selectedStore} onChange={e => { setSelectedStore(e.target.value); fetchData(e.target.value, refYear, refMonth); }}
              style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b", padding: "6px 12px", fontSize: 13, cursor: "pointer", outline: "none" }}>
              <option value="">Todas las sedes</option>
              {data.stores.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
          Cargando datos...
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            {monthKeys.map((mk, i) => {
              const m = data.months[mk];
              return (
                <div key={mk} style={{
                  background: "#f8fafc", borderRadius: 10,
                  padding: "10px 16px", borderLeft: `3px solid ${COLORS[i]}`,
                  flex: "1 1 160px"
                }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", textTransform: "capitalize" }}>{mk}</p>
                  <p style={{ margin: "4px 0 2px", fontSize: 16, fontWeight: 700, color: COLORS[i] }}>
                    ${m.total_month_usd.toLocaleString("en-US")}
                  </p>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {m.peak_day && (
                      <span style={{ fontSize: 10, color: "#94a3b8" }}>↑ Pico: día {m.peak_day}</span>
                    )}
                    {i === 0 && varPct !== null && (
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        color: varPct >= 0 ? "#10b981" : "#f87171",
                        background: varPct >= 0 ? "#064e3b22" : "#7f1d1d22",
                        padding: "1px 5px", borderRadius: 4
                      }}>
                        {varPct >= 0 ? "+" : ""}{varPct}% vs anterior
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
              <YAxis
                tick={{ fill: "#64748b", fontSize: 11 }}
                tickLine={false} axisLine={false}
                tickFormatter={v => `$${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`}
                width={48}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8", paddingTop: 10 }} />
              {peakDay && (
                <ReferenceLine x={peakDay} stroke="#6366f1" strokeDasharray="4 4" opacity={0.5}
                  label={{ value: "Pico", position: "insideTopRight", fill: "#6366f1", fontSize: 10 }} />
              )}
              {monthKeys.map((mk, i) => (
                <Line key={mk} type="monotone" dataKey={mk} name={mk}
                  stroke={COLORS[i]} strokeWidth={i === 0 ? 2.5 : 1.5}
                  dot={false} activeDot={{ r: 5 }}
                  opacity={i === 0 ? 1 : 0.6} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>

          {peakDay && currentMonth?.days?.[peakDay] && (
            <div style={{
              marginTop: 16, background: "#f8fafc", borderRadius: 10,
              padding: "10px 14px", borderLeft: "3px solid #6366f1"
            }}>
              <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                💡 <strong style={{ color: "#c7d2fe" }}>Insight:</strong>{" "}
                El día {peakDay} fue el más movido de {currentMonthKey} con ${currentMonth.days[peakDay].total_usd.toLocaleString("en-US")} en ventas.
                {varPct !== null && (varPct >= 0
                  ? ` Las ventas del mes subieron ${varPct}% respecto al mes anterior.`
                  : ` Las ventas del mes bajaron ${Math.abs(varPct)}% respecto al mes anterior.`
                )}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
